import { gql } from "@medbillai/graphql-types";

import { CURRENT_BENEFITS_SUMMARY_SCHEMA_VERSION } from "@medbillai/schemas";

export const getIssueQueryVariables = (issueId: string, userId: string) => {
  return {
    id: issueId,
    benefitsSummarySchemaVersion: CURRENT_BENEFITS_SUMMARY_SCHEMA_VERSION,
    userId,
  };
};

export const getIssueQuery = gql(/* GraphQL */ `
  query GetIssue($id: ID!, $benefitsSummarySchemaVersion: Int!, $userId: ID!) {
    issue__Issue(filter: { 
      id: { eq: $id },
      owner: { id: { eq: $userId } }
    }) {
      __typename
      id
      title
      openedAt
      closedAt
      caseType
      benefitsQuestionType
      billDisputeType
      additionalDetails
      benefitsInclude
      benefitsPrice
      isEscalated
      benefitsCoverage
      userVisibleEvents(order: { sentAt: { dir: ASC } }) {
        # __typename seems to be needed for the polymorphic query here.
        __typename
        id
        eventType
        participant
        sentAt
        userVisible
        sentByProfile {
          name
        }
        eventFeedbacks (filter:  {owner: {id: {eq: $userId}}}) {
          id
          rating
        }

        ... on issue__DocumentUploadedEvent_Type {
          uploading @client
          document {
            id
            name
            dataType
            createdAt
            latestIngestion {
              endedAt
              resources(filter: { isLatest: { eq: true } }, first: 1) {
                __typename
                id
                ... on fhirext__InvoiceVersion_Type {
                  title
                }
                ... on fhirext__ExplanationOfBenefitVersion_Type {
                  title
                }
              }
            }
            files {
              id
              sizeBytes
              contentType
            }
          }
        }
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__HipaaReleaseRequestedEvent_Type {
          hipaaRelease {
            id
            name
            dateOfBirth
            createdAt
            grantedAt
          }
        }
        ... on issue__BillDisputeEvent_Type {
          eobId
          eob {
            id
            resourceId
            provider {
              display
            }
            facility {
              display
            }
            serviceDate
            decision {
              text
              coding {
                code
              }
            }
            findings(
              filter: {
                and: [
                  {
                    explanation: { exists: true }
                    lastReviewStatus: { eq: ACCEPTED }
                    userDismissed: { eq: false }
                  }
                ]
              }
            ) {
              id
              type
            }
          }
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          release {
            id
            createdAt
            grantedAt
          }
          template {
            id
            templateType
            displayName
            description
          }
        }
        ... on issue__ClosedEvent_Type {
          feedback {
            id
            rating
          }
        }
        ... on issue__PaymentAgreementRequestedEvent_Type {
          userAccepted
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
          chargeDate
        }
        ... on issue__BenefitsSummaryGeneratedEvent_Type {
          id
          sentAt
          plan {
            id
            planLabel
            link {
              id
              endpoint {
                id
                slug
              }
            }
            benefitsSummaryResults(
              filter: { schemaVersion: { eq: $benefitsSummarySchemaVersion } }
            ) {
              id
              summary
            }
          }
        }
        ... on issue__PlanSelectionEvent_Type {
          id
          selectedPlan {
            id
          }
          options {
            id
            planLabel
          }
        }
        ... on issue__UserSelectionRequestedEvent_Type {
          id
          message
          stringOptions: options
        }
        ... on issue__ConnectInsuranceRequestedEvent_Type {
          id
          sentAt
          insuranceLinkMessage
        }
        ... on issue__ConnectAccountRequestedEvent_Type {
          id
          sentAt
          connectAccountMessage: chatBody
          endpointType
          buttonText
          showSkipButton
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__BillPaymentSuccessEvent_Type {
          stripePaymentIntentId
          billPaymentRequest {
            id
          }
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
          errorMessage
          errorIsRetriable
        }
      }
      lastEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
        }
      }
      lastNonUserEvent {
        __typename
        id
        participant
      }
      firstUnseenEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
      }
    }
  }
`);

export const sendMessageMutation = gql(/* GraphQL */ `
  mutation SendMessage($input: CreateIssueChatEventInput!, $userId: ID!) {
    # TODO - ENG-198 - move to a fragment
    createIssueChatEvent(input: $input) {
      __typename
      id
      eventType
      participant
      sentAt
      userVisible
      sentByProfile {
        name
      }
      eventFeedbacks(filter: {owner: {id: {eq: $userId}}}) {
        id
        rating
      }
      ... on issue__ChatEvent_Type {
        chatBody
      }
    }
  }
`);

export const markIssueAsSeenMutation = gql(/* GraphQL */ `
  mutation MarkIssueAsSeen($input: UpdateIssueInput!) {
    updateIssue(input: $input) {
      id
    }
  }
`);

export const selectPlanForEventMutation = gql(/* GraphQL */ `
  mutation selectPlanForEvent($input: SelectPlanForEventInput!) {
    selectPlanForEvent(input: $input) {
      id
    }
  }
`);

export const recordIssueChatEventFeedbackMutation = gql(`
  mutation RecordIssueChatEventFeedback($input: RecordIssueEventFeedbackInput!) {
    recordIssueEventFeedback(input: $input) {
      success
    }
  }
`);

export const getChatEventFeedbackOptionsQuery = gql(/* GraphQL */ `
  query GetChatEventFeedbackOptions($area: feedback__AreaType!) {
    feedback__Option(
      filter: { 
        area: { eq: $area },
        archived: { eq: false }
      },
      order: { order: { dir: ASC } }
    ) {
      id
      value
      sentiment
    }
  }
`);
